import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api/client';
import { getSocket, disconnectSocket } from '../api/socket';
import { useAuth } from '../context/AuthContext';
import './VideoSession.css';

// Public STUN server only (no TURN) - fine for most direct/same-network
// demo connections. Add a TURN server before relying on this across
// strict corporate/mobile NATs in production.
const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

export default function VideoSession() {
  const { appointmentId } = useParams();
  const { user } = useAuth();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const socketRef = useRef(null);
  const localStreamRef = useRef(null);

  const [connected, setConnected] = useState(false);
  const [remoteJoined, setRemoteJoined] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const chatBottomRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function setup() {
      try {
        // 1. Load chat history
        const history = await apiClient.get(`/messages/${appointmentId}`);
        if (!cancelled) setMessages(history.data.messages);

        // 2. Get local media (falls back to audio-only if camera unavailable)
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
          setCamOn(false);
        }
        if (cancelled) return;
        localStreamRef.current = stream;
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;

        // 3. Peer connection
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        pcRef.current = pc;
        stream.getTracks().forEach((track) => pc.addTrack(track, stream));

        pc.ontrack = (event) => {
          if (remoteVideoRef.current) remoteVideoRef.current.srcObject = event.streams[0];
          setRemoteJoined(true);
        };

        pc.onicecandidate = (event) => {
          if (event.candidate) {
            socketRef.current?.emit('webrtc-signal', { appointmentId, signal: event.candidate });
          }
        };

        // 4. Socket connection + signaling
        const socket = getSocket();
        socketRef.current = socket;
        socket.connect();

        socket.on('connect', () => {
          socket.emit('join-session', { appointmentId }, (res) => {
            if (res?.error) setError(res.error);
            else setConnected(true);
          });
        });

        socket.on('peer-joined', async () => {
          // I was already here - I'm the offer initiator.
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          socket.emit('webrtc-signal', { appointmentId, signal: pc.localDescription });
        });

        socket.on('webrtc-signal', async ({ signal }) => {
          if (signal.type === 'offer') {
            await pc.setRemoteDescription(new RTCSessionDescription(signal));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            socket.emit('webrtc-signal', { appointmentId, signal: pc.localDescription });
          } else if (signal.type === 'answer') {
            await pc.setRemoteDescription(new RTCSessionDescription(signal));
          } else if (signal.candidate) {
            try {
              await pc.addIceCandidate(new RTCIceCandidate(signal));
            } catch (e) { /* ignore late candidates */ }
          }
        });

        socket.on('peer-left', () => setRemoteJoined(false));

        socket.on('chat-message', (msg) => {
          setMessages((m) => [...m, msg]);
        });

        socket.on('connect_error', () => setError('Could not connect to the session server.'));
      } catch (err) {
        setError('Could not access camera/microphone. Please check permissions.');
      }
    }

    setup();

    return () => {
      cancelled = true;
      pcRef.current?.close();
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      socketRef.current?.emit('leave-session');
      disconnectSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function toggleMic() {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (track) { track.enabled = !track.enabled; setMicOn(track.enabled); }
  }
  function toggleCam() {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (track) { track.enabled = !track.enabled; setCamOn(track.enabled); }
  }
  function sendChat(e) {
    e.preventDefault();
    if (!chatInput.trim()) return;
    socketRef.current?.emit('chat-message', { appointmentId, content: chatInput });
    setChatInput('');
  }

  return (
    <div className="video-page">
      <div className="container video-inner">
        <div className="video-main">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="video-tiles">
            <div className="video-tile">
              <video ref={remoteVideoRef} autoPlay playsInline className="video-el" />
              {!remoteJoined && <div className="video-placeholder">Waiting for the other participant to join…</div>}
            </div>
            <video ref={localVideoRef} autoPlay playsInline muted className="video-self" />
          </div>

          <div className="video-controls">
            <button className={`ctrl-btn ${micOn ? '' : 'off'}`} onClick={toggleMic}>{micOn ? 'Mute' : 'Unmute'}</button>
            <button className={`ctrl-btn ${camOn ? '' : 'off'}`} onClick={toggleCam}>{camOn ? 'Camera Off' : 'Camera On'}</button>
            <Link to="/dashboard" className="ctrl-btn leave">Leave Session</Link>
          </div>
          <p className="video-status">{connected ? 'Connected to session' : 'Connecting…'}</p>
        </div>

        <div className="card video-chat">
          <h3>Session Chat</h3>
          <div className="video-chat-messages">
            {messages.map((m) => (
              <div key={m.id} className={`chat-bubble ${m.senderId === user?.id ? 'mine' : ''}`}>
                <span className="chat-sender">{m.senderId === user?.id ? 'You' : (m.senderName || m.senderRole)}</span>
                <p>{m.content}</p>
              </div>
            ))}
            <div ref={chatBottomRef} />
          </div>
          <form className="video-chat-input" onSubmit={sendChat}>
            <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="Type a message…" />
            <button className="btn btn-primary" type="submit">Send</button>
          </form>
        </div>
      </div>
    </div>
  );
}
